import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../Context/AuthContext';

import {
  LogOut, User, Palette, Scissors, Clock,
  Users, CheckCircle, Calendar as CalendarIcon, Sparkles, ArrowLeft,
  Heart, ShoppingCart, ArrowRight, ShoppingBag, Package
} from 'lucide-react';
import CalendlyButton from '../components/CalendlyButton';
import Header from '../components/Header';

import './Home.css';
import './Gallery.css';
import { productService } from '../services/productService';

// Calendly Configuration - Single URL for both appointment types
const CALENDLY_URL = 'https://calendly.com/akostellatest/fitting-session';

const INITIAL_FITTING_DETAILS = {
  itemsToStitch: '',
  dueDate: '',
  length: '', yokeLength: '', shoulder: '', shoulderWidth: '',
  frontNeck: '', backNeck: '', dart: '', backWidth: '', frontWidth: '',
  chest: '', bust: '', waist: '', hip: '',
  sleeveLength: '', sleeveOpen: '', armPit: '', armHole: '', collar: '',
  slitLength: '', pantSkirtLength: '', thighRound: '', kneeRound: '',
  calfRound: '', ankleRound: ''
};

const FITTING_CONFIG = {
  'Kurti': {
    'UPPER BODY MEASUREMENTS': [
      { key: 'length', label: 'Total Length' },
      { key: 'yokeLength', label: 'Yoke Length' },
      { key: 'shoulder', label: 'Shoulder' },
      { key: 'frontNeck', label: 'Front Neck' },
      { key: 'backNeck', label: 'Back Neck' },
      { key: 'dart', label: 'Dart' }
    ],
    'BODY MEASUREMENTS': [
      { key: 'chest', label: 'Chest' },
      { key: 'bust', label: 'Bust' },
      { key: 'waist', label: 'Waist' },
      { key: 'hip', label: 'Hip' }
    ],
    'SLEEVE MEASUREMENTS': [
      { key: 'sleeveLength', label: 'Sleeve Length' },
      { key: 'sleeveOpen', label: 'Sleeve Open' },
      { key: 'armHole', label: 'Arm Hole' }
    ]
  },
  'Lehanga': {
    'UPPER BODY MEASUREMENTS': [
      { key: 'yokeLength', label: 'Yoke Length' },
      { key: 'shoulder', label: 'Shoulder' },
      { key: 'frontNeck', label: 'Front Neck' },
      { key: 'backNeck', label: 'Back Neck' },
      { key: 'dart', label: 'Dart' }
    ],
    'BODY MEASUREMENT': [
      { key: 'chest', label: 'Chest' },
      { key: 'bust', label: 'Bust' },
      { key: 'waist', label: 'Waist' },
      { key: 'hip', label: 'Hip' }
    ],
    'SLEEVE MEASUREMENTS': [
      { key: 'sleeveLength', label: 'Sleeve Length' },
      { key: 'sleeveOpen', label: 'Sleeve Open' },
      { key: 'armHole', label: 'Arm Hole' }
    ],
    'LOWER BODY MEASUREMENTS': [
      { key: 'pantSkirtLength', label: 'Skirt Length' },
      { key: 'thighRound', label: 'Thigh Round' }
    ]
  },
  'Half Saree': {
    'UPPER BODY MEASUREMENTS': [
      { key: 'yokeLength', label: 'Yoke Length' },
      { key: 'shoulder', label: 'Shoulder' },
      { key: 'frontNeck', label: 'Front Neck' },
      { key: 'backNeck', label: 'Back Neck' },
      { key: 'dart', label: 'Dart' }
    ],
    'BODY MEASUREMENT': [
      { key: 'chest', label: 'Chest' },
      { key: 'bust', label: 'Bust' },
      { key: 'waist', label: 'Waist' },
      { key: 'hip', label: 'Hip' }
    ],
    'SLEEVE MEASUREMENTS': [
      { key: 'sleeveLength', label: 'Sleeve Length' },
      { key: 'sleeveOpen', label: 'Sleeve Open' },
      { key: 'armHole', label: 'Arm Hole' }
    ],
    'LOWER BODY MEASUREMENTS': [
      { key: 'pantSkirtLength', label: 'Skirt Length' },
      { key: 'thighRound', label: 'Thigh Round' }
    ]
  },
  'Skirt & Top': {
    'UPPER BODY MEASUREMENTS': [
      { key: 'yokeLength', label: 'Yoke Length' },
      { key: 'shoulder', label: 'Shoulder' },
      { key: 'frontNeck', label: 'Front Neck' },
      { key: 'backNeck', label: 'Back Neck' },
      { key: 'dart', label: 'Dart' }
    ],
    'BODY MEASUREMENT': [
      { key: 'chest', label: 'Chest' },
      { key: 'bust', label: 'Bust' },
      { key: 'waist', label: 'Waist' },
      { key: 'hip', label: 'Hip' }
    ],
    'SLEEVE MEASUREMENTS': [
      { key: 'sleeveLength', label: 'Sleeve Length' },
      { key: 'sleeveOpen', label: 'Sleeve Open' },
      { key: 'armHole', label: 'Arm Hole' }
    ],
    'LOWER BODY MEASUREMENTS': [
      { key: 'pantSkirtLength', label: 'Skirt Length' },
      { key: 'thighRound', label: 'Thigh Round' }
    ]
  },
  'Blouse': {
    'UPPER BODY MEASUREMENTS': [
      { key: 'length', label: 'Length (Total)' },
      { key: 'shoulder', label: 'Shoulder' },
      { key: 'frontNeck', label: 'Front Neck' },
      { key: 'backNeck', label: 'Back Neck' }
    ],
    'BODY MEASUREMENT': [
      { key: 'dart', label: 'Dart' },
      { key: 'chest', label: 'Chest' },
      { key: 'bust', label: 'Bust' },
      { key: 'waist', label: 'Waist' }
    ],
    'SLEEVE MEASUREMENTS': [
      { key: 'sleeveLength', label: 'Sleeve Length' },
      { key: 'sleeveOpen', label: 'Sleeve Open' },
      { key: 'armHole', label: 'Arm Hole' },
      { key: 'collar', label: 'Collar' }
    ]
  },
  'Gown': {
    'UPPER BODY MEASUREMENTS': [
      { key: 'length', label: 'Length (Total)' },
      { key: 'yokeLength', label: 'Yoke Length' },
      { key: 'shoulder', label: 'Shoulder' },
      { key: 'frontNeck', label: 'Front Neck' },
      { key: 'backNeck', label: 'Back Neck' },
      { key: 'dart', label: 'Dart' }
    ],
    'BODY MEASUREMENTS': [
      { key: 'chest', label: 'Chest' },
      { key: 'bust', label: 'Bust' },
      { key: 'hip', label: 'Hip' },
      { key: 'waist', label: 'Waist' }
    ],
    'SLEEVE MEASUREMENTS': [
      { key: 'sleeveLength', label: 'Sleeve Length' },
      { key: 'sleeveOpen', label: 'Sleeve Open' },
      { key: 'armHole', label: 'Arm Hole' }
    ],
    'LOWER BODY MEASUREMENTS': [
      { key: 'thighRound', label: 'Thigh Round' }
    ]
  }
};

// Sanitize measurement input: allow only numbers with up to 2 decimal places
const sanitizeMeasurementInput = (value) => {
  // Remove all characters except digits and decimal point
  let sanitized = value.replace(/[^0-9.]/g, '');

  // Ensure only one decimal point exists
  const parts = sanitized.split('.');
  if (parts.length > 2) {
    sanitized = parts[0] + '.' + parts.slice(1).join('');
  }

  // Limit to 3 digits before decimal point
  if (parts[0].length > 3) {
    parts[0] = parts[0].slice(0, 3);
    sanitized = parts.join('.');
  }

  // Limit to 2 decimal places
  if (parts.length === 2 && parts[1].length > 2) {
    sanitized = parts[0] + '.' + parts[1].slice(0, 2);
  }

  return sanitized;
};

const Home = ({ savedItems = [], cartItems = [],addToCart,addFavorite,removeFavorite
 }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [notes, setNotes] = useState('');
  const [isCm, setIsCm] = useState(true);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  useEffect(() => {
    if (location.state?.openAppointment === 'design') {
      setSelectedAppointment('design');
    } else if (location.state?.openAppointment === 'fitting') {
      setSelectedAppointment('fitting');
    }
  }, [location.state]);
useEffect(() => {
  const fetchProducts = async () => {
    try {
      const result = await productService.getAllProducts();
      if (result.success) {
        const mapped = result.data.map(p => ({
          id: p.id,
          title: p.name,
          price: `Rs ${p.price}`, // Placeholder for original price
          salePrice: `Rs ${p.sale_price}`, // Placeholder for sale price
          image: p.image_url
        }));
        setProducts(mapped);
      }
    } catch (err) {
      console.error('Failed to load products', err);
    } finally {
      setProductsLoading(false);
    }
  };

  fetchProducts();
}, []);
  const [fittingDetails, setFittingDetails] = useState({
    itemsToStitch: '',
    dueDate: '',
    length: '', yokeLength: '', shoulder: '', shoulderWidth: '',
    frontNeck: '', backNeck: '', dart: '', backWidth: '', frontWidth: '',
    chest: '', bust: '', waist: '', hip: '',
    sleeveLength: '', sleeveOpen: '', armPit: '', armHole: '', collar: '',
    slitLength: '', pantSkirtLength: '', thighRound: '', kneeRound: '',
    calfRound: '', ankleRound: ''
  });

  const resetAppointmentForm = () => {
    setSelectedAppointment(null);
    setNotes('');
    setFittingDetails(INITIAL_FITTING_DETAILS);

    setIsCm(true);
    setShowSuccess(false);
  };

  const [showSuccess, setShowSuccess] = useState(false);
const isSaved = (productId) => {
  return savedItems.some(item => item.product_id === productId);
};

const isInCart = (productId) => {
  return cartItems.some(item => item.id === productId);
};

const handleSave = (product, e) => {
  e.stopPropagation();

  // ✅ STEP 5: Login check
  if (!user) {
    navigate('/login', {
      state: { redirectTo: '/home' }
    });
    return;
  }

  if (isSaved(product.id)) {
    removeFavorite(product);
  } else {
    addFavorite(product);
  }
};

const handleCartAction = (product, e) => {
  e.stopPropagation();

  // ✅ STEP 5: Login check
  if (!user) {
    navigate('/login', {
      state: { redirectTo: '/home' }
    });
    return;
  }

  if (isInCart(product.id)) {
    navigate('/cart', { state: { from: '/home' } });
  } else {
    addToCart(product);
  }
};
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleBookAppointment = (type) => {
    resetAppointmentForm();
    setSelectedAppointment(type);
  };


  const handleFittingDetailChange = (field, value) => {
    if (field === 'itemsToStitch') {
      setFittingDetails({
        ...INITIAL_FITTING_DETAILS,
        itemsToStitch: value,
        dueDate: fittingDetails.dueDate
      });
    } else if (field === 'dueDate') {
      setFittingDetails(prev => ({
        ...prev,
        dueDate: value
      }));
    } else {
      // For all measurement fields, sanitize to allow only numbers with up to 2 decimal places
      const sanitizedValue = sanitizeMeasurementInput(value);
      setFittingDetails(prev => ({
        ...prev,
        [field]: sanitizedValue
      }));
    }
  };

  const handleBackToHome = () => {
    // If the user arrived here explicitly to open an appointment (e.g., from the Gallery),
    // navigating back should take them back to the previous page in history.
    if (location.state?.openAppointment) {
      navigate(-1);
    } else {
      // Otherwise, just reset the local state to close the appointment overlay.
      resetAppointmentForm();
    }
  };

  const renderHeader = () => {
    let leftAction;
    let rightAction;

    if (selectedAppointment || showSuccess) {
      leftAction = (
        <button onClick={handleBackToHome} className="back-nav">
          <ArrowLeft size={18} />
          <span className="nav-text">Back</span>
        </button>
      );
      rightAction = null;
    } else {
      leftAction = (
        user && (<button onClick={handleLogout} className="logout-button">
          <LogOut size={20} />
          Logout
        </button>)
      );
      rightAction = (
        <div className="header-actions">
          {!user && (<button className="tracking-btn" onClick={() => navigate('/login', { state: { from: '/home' } })}>
                   Login
           </button>)}
          <button className="tracking-btn" onClick={() => navigate('/tracking', { state: { from: '/home' } })}>
           <Package size={20} />
          </button>
          <button className="header-icon-btn" aria-label="Wishlist" onClick={() => navigate('/favourites', { state: { from: '/', label: 'Home' } })}>
            <Heart size={20} />
            {savedItems.length > 0 && (
              <span className="heart-badge">{savedItems.length}</span>
            )}
          </button>
          <button className="header-icon-btn" aria-label="Cart" onClick={() => navigate('/cart', { state: { from: '/home' } })}>
            <ShoppingCart size={20} />
            {cartItems?.length > 0 && (
              <span className="cart-badge">{cartItems.reduce((sum, item) => sum + item.quantity, 0)}</span>
            )}
          </button>
        </div>
      );
    }

    return <Header leftAction={leftAction} rightAction={rightAction} />;
  };

  // Build Calendly options with prefilled form data
  const buildCalendlyOptions = () => {
    const unit = isCm ? 'cm' : 'inches';
    const appointmentType = selectedAppointment === 'design' ? 'Design Discussion' : 'Fitting Session';

    // For Design Discussion, only prefill basic info and notes
    if (selectedAppointment === 'design') {
      return {
        prefill: {
          name: user.fullName || '',
          email: user.email || '',
          customAnswers: {
            a1: appointmentType,  // Appointment Type
            a8: notes || 'None'   // Additional Notes (a8 since measurements are a2-a7)
          }
        }
      };
    }

    // For Fitting Session, prefill all measurement data
    return {
      prefill: {
        name: user.fullName || '',
        email: user.email || '',
        customAnswers: {
          a1: appointmentType,  // Appointment Type (NEW - Question 1)
          a2: fittingDetails.itemsToStitch || 'Not specified',  // Item to Stitch (shifted from a1)
          a3: fittingDetails.dueDate || 'Flexible',  // Due Date (shifted from a2)
          a4: [  // Upper Body Measurements (shifted from a3)
            fittingDetails.length && `Length: ${fittingDetails.length} ${unit}`,
            fittingDetails.yokeLength && `Yoke Length: ${fittingDetails.yokeLength} ${unit}`,
            fittingDetails.shoulder && `Shoulder: ${fittingDetails.shoulder} ${unit}`,
            fittingDetails.frontNeck && `Front Neck: ${fittingDetails.frontNeck} ${unit}`,
            fittingDetails.backNeck && `Back Neck: ${fittingDetails.backNeck} ${unit}`,
            fittingDetails.dart && `Dart: ${fittingDetails.dart} ${unit}`
          ].filter(Boolean).join('\n') || 'Not provided',
          a5: [  // Body Measurements (shifted from a4)
            fittingDetails.chest && `Chest: ${fittingDetails.chest} ${unit}`,
            fittingDetails.bust && `Bust: ${fittingDetails.bust} ${unit}`,
            fittingDetails.waist && `Waist: ${fittingDetails.waist} ${unit}`,
            fittingDetails.hip && `Hip: ${fittingDetails.hip} ${unit}`
          ].filter(Boolean).join('\n') || 'Not provided',
          a6: [  // Sleeve Measurements (shifted from a5)
            fittingDetails.sleeveLength && `Sleeve Length: ${fittingDetails.sleeveLength} ${unit}`,
            fittingDetails.sleeveOpen && `Sleeve Open: ${fittingDetails.sleeveOpen} ${unit}`,
            fittingDetails.armHole && `Arm Hole: ${fittingDetails.armHole} ${unit}`,
            fittingDetails.collar && `Collar: ${fittingDetails.collar} ${unit}`
          ].filter(Boolean).join('\n') || 'Not provided',
          a7: [  // Lower Body Measurements (shifted from a6)
            fittingDetails.pantSkirtLength && `Skirt/Pant Length: ${fittingDetails.pantSkirtLength} ${unit}`,
            fittingDetails.thighRound && `Thigh Round: ${fittingDetails.thighRound} ${unit}`,
            fittingDetails.kneeRound && `Knee Round: ${fittingDetails.kneeRound} ${unit}`,
            fittingDetails.calfRound && `Calf Round: ${fittingDetails.calfRound} ${unit}`,
            fittingDetails.ankleRound && `Ankle Round: ${fittingDetails.ankleRound} ${unit}`
          ].filter(Boolean).join('\n') || 'Not provided',
          a8: notes || 'None'  // Additional Notes (shifted from a7)
        }
      }
    };
  };



  if (showSuccess) {
    return (
      <div className="home-container">
        {renderHeader()}
        <div className="success-modal fade-in">
          <div className="success-icon">
            <CheckCircle size={64} color="#10b981" />
          </div>
          <h2>Appointment Booked!</h2>
          <p>We'll see you soon</p>
        </div>
      </div>
    );
  }

  if (selectedAppointment) {
    return (
      <div className="home-container">
        {renderHeader()}
        <div className="appointment-booking fade-in">
          <div className="booking-header">
            <div className="booking-icon">
              {selectedAppointment === 'design' ? (
                <Palette size={24} color="#fff" />
              ) : (
                <Scissors size={24} color="#fff" />
              )}
            </div>
            <div className="booking-header-text">
              <h2>
                {selectedAppointment === 'design'
                  ? 'Design Discussion'
                  : 'Fitting Session'}
              </h2>
              <p>
                {selectedAppointment === 'design'
                  ? 'Share your style vision with our designers'
                  : 'Provide your measurements for a perfect fit'}
              </p>
            </div>
          </div>
          {/* ... existing form content ... */}

          <div className="booking-form">


            {
              selectedAppointment === 'fitting' && (
                <div className="fitting-details-section fade-in">
                  <div className="fitting-header">
                    <h3>Fitting Details (All Optional)</h3>
                    <div className="unit-toggle">
                      <span className="unit-text">Unit:</span>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={isCm}
                          onChange={(e) => setIsCm(e.target.checked)}
                        />
                        <span className="slider round"></span>
                      </label>
                      <span className="unit-text active">CM</span>
                    </div>
                  </div>

                  <div className="measurement-grid">
                    <div className="measurement-group">
                      <label>Item to Stitch</label>
                      <select
                        value={fittingDetails.itemsToStitch}
                        onChange={(e) => handleFittingDetailChange('itemsToStitch', e.target.value)}
                      >
                        <option value="">Select item</option>
                        <option value="Blouse">Blouse</option>
                        <option value="Gown">Gown</option>
                        <option value="Half Saree">Half Saree</option>
                        <option value="Kurti">Kurti</option>
                        <option value="Lehanga">Lehanga</option>
                        <option value="Skirt & Top">Skirt & Top</option>
                      </select>
                    </div>

                    <div className="measurement-group">
                      <label>Suggested Delivery Date</label>
                      <input
                        type="date"
                        value={fittingDetails.dueDate}
                        onChange={(e) => handleFittingDetailChange('dueDate', e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>

                  {fittingDetails.itemsToStitch && FITTING_CONFIG[fittingDetails.itemsToStitch] && (
                    Object.entries(FITTING_CONFIG[fittingDetails.itemsToStitch]).map(([section, fields]) => (
                      <React.Fragment key={section}>
                        <h4>{section}</h4>
                        <div className="measurement-grid">
                          {fields.map(field => (
                            <div className="measurement-group" key={field.key}>
                              <label>{field.label}</label>
                              <input
                                type="text"
                                placeholder={isCm ? "in cm" : "in inches"}
                                value={fittingDetails[field.key] || ''}
                                onChange={(e) => handleFittingDetailChange(field.key, e.target.value)}
                              />
                            </div>
                          ))}
                        </div>
                      </React.Fragment>
                    ))
                  )}
                </div>
              )
            }

            <div className="form-section">
              <label>
                {selectedAppointment === 'design'
                  ? 'Share Your Design Vision (Optional)'
                  : 'Additional Notes (Optional)'}
              </label>
              <textarea
                placeholder="Any special requirements or preferences..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows="4"
              />
            </div>

            <CalendlyButton
              calendlyUrl={CALENDLY_URL}
              buttonText={selectedAppointment === 'fitting' ? 'Book Fitting Session' : 'Book Design Discussion'}
              className="btn-confirm"
              calendlyOptions={buildCalendlyOptions()}
              onSuccess={resetAppointmentForm}
              appointmentType={selectedAppointment === 'design' ? 'Design Discussion' : 'Fitting Session'}
            />
          </div >
        </div >
      </div >
    );
  }

  return (
    <div className="home-container">
      {renderHeader()}

      <div className="welcome-banner fade-in">
        <User size={48} />
        <div>
          <h2>Welcome {user && (user.name)}!</h2>
          <p>{user && (user.email)}</p>

        </div>
      </div>

      <div className="main-content">

{/* Sale Products Section */}
        <div className="mb-8">
          <div className="sale-section-container">
                      <div className="sale-header">
                        <div className="sale-header-content">
                          <div className="sale-title-wrapper">
                            <h3 className="sale-title">Sale</h3>
                            <span className="sale-badge">Limited Time</span>
                          </div>
                          <p className="sale-description">Exclusive discounts on selected designs</p>
                        </div>
                        <button
                          onClick={() => navigate('/gallery')}
                          className="view-more-button"
                        >
                          View More
                          <ArrowRight className="view-more-icon" />
                        </button>
                      </div>

                      {/* Sale Products Grid */}
                        {/* Product 1 */}
                        <div className="sale-products-grid home-products">
                        {!productsLoading &&
                          products.slice(0, 4).map((product, index) => {
                            const saved = isSaved(product.id);
                            const inCart = isInCart(product.id);

                            return (
                              <div
                                key={product.id}
                                className={`design-card fade-in stagger-${index + 1}`}
                                onClick={() =>
                                  navigate('/gallery', {
                                    state: { productId: product.id, from: '/home' }
                                  })
                                }
                              >

<div className="product-image-container">
  <img
    src={product.image}
    alt={product.title}
    className="product-image"
  />

  <div className="product-sale-badge">SALE</div>

  {/* Hover Overlay */}
  <div className="design-hover-overlay">
    <div className="design-actions">
      <button
        className={`btn-save ${saved ? 'active' : ''}`}
        onClick={(e) => handleSave(product, e)}
      >
        <Heart size={18} fill={saved ? 'currentColor' : 'none'} />
        {saved ? 'Saved' : 'Save'}
      </button>

      <button
        className={`btn-add-cart ${inCart ? 'in-cart' : ''}`}
        onClick={(e) => handleCartAction(product, e)}
      >
        <ShoppingBag size={18} />
        {inCart ? 'Go to Cart' : 'Add to Cart'}
      </button>
    </div>
  </div>

                                  {/* ✅ HOVER OVERLAY (same UX as Gallery) */}
                                  <div className="design-hover-overlay">
                                    <div className="design-actions">
                                      <button
                                        className={`btn-save ${saved ? 'active' : ''}`}
                                        onClick={(e) => handleSave(product, e)}
                                      >
                                        <Heart size={18} fill={saved ? 'currentColor' : 'none'} />
                                        {saved ? 'Saved' : 'Save'}
                                      </button>

                                      <button
                                        className={`btn-add-cart ${inCart ? 'in-cart' : ''}`}
                                        onClick={(e) => handleCartAction(product, e)}
                                      >
                                        <ShoppingBag size={18} />
                                        {inCart ? 'Go to Cart' : 'Add to Cart'}
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                <div className="product-info">
                                  <h4 className="product-title">{product.title}</h4>
                                  <div className="product-pricing">
                                    <span className="original-price">{product.price}</span>
                                    <span className="sale-price">{product.salePrice}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>



                    </div>
        </div>


        <div className="section-header">
          <h2>Book an Appointment</h2>
        </div>

        <div className="appointment-cards">
          <div className="appointment-card scale-in stagger-1" onClick={() => handleBookAppointment('design')}>
            <div className="card-icon purple">
              <Palette size={24} />
            </div>
            <h3>Design Discussion</h3>
            <p>Consult with our expert designers to bring your vision to life. Share your ideas, preferences, and get personalized recommendations.</p>
            <div className="card-info">
              <div className="info-item">
                <Clock size={16} />
                <span>60 minutes session</span>
              </div>
              <div className="info-item">
                <Users size={16} />
                <span>One-on-one consultation</span>
              </div>
            </div>
            <button className="btn-book purple">
              <CalendarIcon size={18} />
              Book Now
            </button>
          </div>

          <div className="appointment-card scale-in stagger-2" onClick={() => handleBookAppointment('fitting')}>
            <div className="card-icon pink">
              <Scissors size={24} />
            </div>
            <h3>Fitting Session</h3>
            <p>Ensure the perfect fit for your custom designs. Our tailors will make precise adjustments to match your measurements.</p>
            <div className="card-info">
              <div className="info-item">
                <Clock size={16} />
                <span>45 minutes session</span>
              </div>
              <div className="info-item">
                <Scissors size={16} />
                <span>Professional tailoring</span>
              </div>
            </div>
            <button className="btn-book pink">
              <CalendarIcon size={18} />
              Book Now
            </button>
          </div>
        </div>

        <div className="features-section fade-in stagger-3">
          <h3>Why Choose Us?</h3>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon purple-bg">
                <Palette size={24} />
              </div>
              <h4>Expert Designers</h4>
              <p>Our team of skilled designers brings years of experience in fashion and style.</p>
            </div>

            <div className="feature-item">
              <div className="feature-icon pink-bg">
                <Scissors size={24} />
              </div>
              <h4>Custom Tailoring</h4>
              <p>Every piece is tailored to your exact measurements for a perfect fit.</p>
            </div>

            <div className="feature-item">
              <div className="feature-icon pink-bg">
                <CheckCircle size={24} />
              </div>
              <h4>Quality Assured</h4>
              <p>Premium fabrics and meticulous attention to detail in every creation.</p>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default Home;
